import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { archiveBoard } from "../../../../../../services/apiBoards";
import { objectToFormData } from "../../../../../../utils";
import { TooltipName } from "../../../../../../utils/Enums";
import { useBoardMutationDialog } from "../../../hooks/useBoardMutationDialog";

export function useArchiveBoardState({
  boardId,
  isActive,
}) {
  const queryClient = useQueryClient();
  const [isBoardCurrentlyActive, setIsBoardCurrentlyActive] =
    useState(isActive);

  useEffect(() => {
    setIsBoardCurrentlyActive(isActive);
  }, [isActive]);

  const archiveBoardDialog = useBoardMutationDialog({
    apiFn: archiveBoard,
    invalidateKeys: ["boards"],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boardName", boardId],
      });
      setIsBoardCurrentlyActive((currentValue) => !currentValue);
    },
    onError: (error) => {
      console.error("Error updating board archive status", error);
    },
  });

  const isArchiveAction = isBoardCurrentlyActive;

  const tooltipTitle = useMemo(
    () =>
      isArchiveAction
        ? TooltipName.MANAGE_BOARD.ARCHIVE_THIS_BOARD
        : TooltipName.MANAGE_BOARD.ACTIVE_THIS_BOARD,
    [isArchiveAction]
  );

  const dialogButtonText = isArchiveAction ? "Archive" : "Restore";
  const dialogTitle = isArchiveAction
    ? "Confirmation: Archive Board"
    : "Confirmation: Restore Board";
  const dialogMessage = isArchiveAction
    ? "Are you sure you want to archive this board? Archiving the board will hide it from view and move it to the archive section. You can always restore it later if needed. Please confirm your action."
    : "Are you sure you want to restore the board ?";

  const submitArchiveStatusChange = useCallback(() => {
    const archiveRequestData = objectToFormData({
      is_archive: isBoardCurrentlyActive,
      board_id: boardId,
    });

    archiveBoardDialog.submit({
      id: boardId,
      data: archiveRequestData,
    });
  }, [archiveBoardDialog, boardId, isBoardCurrentlyActive]);

  return {
    archiveBoardDialog,
    dialogButtonText,
    dialogMessage,
    dialogTitle,
    isBoardCurrentlyActive,
    submitArchiveStatusChange,
    tooltipTitle,
  };
}
